import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Plus } from 'lucide-react';
import {
  QnAKbForm,
  getQnaKbFormSchema,
  QnaKbFormValues,
  QnaKbFormDefaultValues,
} from '../qna-kb-form';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAddQnA } from '@/modules/gpt-chats/hooks/use-kb';

import { useToast } from '@/hooks/use-toast';
import { isErrorWithErrors } from '@/modules/gpt-chats/utils/error';
import { useState } from 'react';

type AddKbQaModalProps = {
  folderId: string;
};

export const AddKbQaModal = ({ folderId }: AddKbQaModalProps) => {
  const { t } = useTranslation();
  const form = useForm<QnaKbFormValues>({
    defaultValues: QnaKbFormDefaultValues,
    resolver: zodResolver(getQnaKbFormSchema(t)),
  });

  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const { mutateAsync, isPending } = useAddQnA();

  const submitHandler = async (values: QnaKbFormValues) => {
    try {
      if (!folderId) return toast.error({ errors: t('SOMETHING_WENT_WRONG') });
      const res = await mutateAsync({
        ...values,
        folder_id: folderId,
      });
      if (!res.is_success) return toast.error({ errors: res.error });
      toast.success(t('NEW_Q_AND_A_ADDED'));
      form.reset();
      setOpen(false);
    } catch (error) {
      if (isErrorWithErrors(error)) return toast.error({ errors: error.errors });
      toast.error({ errors: t('SOMETHING_WENT_WRONG') });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          {t('ADD_Q_AND_A')}
        </Button>
      </DialogTrigger>
      <DialogContent className="md:min-w-[720px]">
        <DialogHeader>
          <DialogTitle>{t('ADD_Q_AND_A')}</DialogTitle>
          <DialogDescription>{t('TRAIN_AI_DESCRIPTION')}</DialogDescription>
        </DialogHeader>
        <QnAKbForm form={form} onSubmit={submitHandler} isSaving={isPending} mode="add" />
      </DialogContent>
    </Dialog>
  );
};
